import { Component, DoCheck, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { Siterecord } from 'app/models';
import {
  ComputationMethodType,
  ContractType,
  EquipmentType,
  IntendedUsageType,
  TranslatableIntendedUsageType
} from 'app/models/enums';
import { AuthorizationService, SiterecordService } from 'app/services';
import { CbmInputStyle } from 'app/shared/input/cbm-input-style';
import { CbmInputTextComponent } from 'app/shared/input/cbm-input-text/cbm-input-text.component';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { enumValues } from 'app/shared/util/enumValues';
import { IntendedUsageUtil } from 'app/utils/intended-usage.util';
import { environment } from 'environments/environment';

@Component({
  selector: 'linear-details-general',
  templateUrl: './linear-details-general.component.html',
  styleUrls: ['./linear-details-general.component.scss']
})
export class LinearDetailsGeneralComponent implements OnInit, DoCheck {
  @Input() id: string;
  @Input() readonly: boolean;
  @Input() inShapeFileReadonly: boolean;
  @Input() inputStyle: CbmInputStyle;
  @Input() location: Siterecord;
  @Input() isRecordIdEditable: boolean;
  @Output() recordIdEditWarning: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(MessageboxComponent) messagebox: MessageboxComponent;
  @ViewChild('linearDetailsGeneralForm') public form: NgForm = null;
  @ViewChild('lineShapeLengthInput') lineShapeLengthCbmInput: CbmInputTextComponent;

  public threeColumnStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-4' };
  public recordIdStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-6' };

  public computationMethods: any;
  public equipmentTypes: any;

  public startLatitude: string;
  public startLongitude: string;
  public endLatitude: string;
  public endLongitude: string;
  public initialRecordId: string;
  public recordIdFeatureEnabled = environment.recordIdFeatureEnabled;
  public tooltipTranslation: string;
  selectableIntendedUsageTypes: TranslatableIntendedUsageType[];
  selectedIntendedUsageType: TranslatableIntendedUsageType;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly siterecordService: SiterecordService,
    private readonly authorizationService: AuthorizationService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.lineShapeLengthCbmInput.registerFormatter(value => (value ? parseFloat(value).toFixed(2) : value));
    this.initialRecordId = this.location.recordId; // Get the value of the initial recordId to compare to ngModel.
    this.initialiseEnums();
    this.initializeTranslations();
    this.initializeIntendedUsageTypes();
  }

  ngDoCheck() {
    this.setCoordinates();
  }

  private initialiseEnums(): void {
    this.computationMethods = enumValues(ComputationMethodType);
    this.equipmentTypes = enumValues(EquipmentType);
  }

  private setCoordinates(): void {
    const coordinates = this.location.routingPoints.split('|');
    const startCoordinates = coordinates[0].split('\\,');
    const endCoordinates = coordinates[coordinates.length - 1].split('\\,');
    this.startLatitude = startCoordinates[1];
    this.startLongitude = startCoordinates[0];
    this.endLatitude = endCoordinates[1];
    this.endLongitude = endCoordinates[0];
  }

  protected initializeTranslations(): void {
    this.translateService
      .get('SITERECORD.RECORD_ID.UNSAVED_CHANGES_WARNING')
      .subscribe(translation => (this.tooltipTranslation = translation));
  }

  public isAdminOrContractTypeDataProvider() {
    return (
      this.authorizationService.isLocationAdmin() || this.location.contract.contractType === ContractType.DataProvider
    );
  }

  public isReadonly(): boolean {
    return (
      this.readonly ||
      (this.authorizationService.isLocationAdmin() && this.location.contract.contractType === ContractType.FCD)
    );
  }

  protected isLocationAdmin(): boolean {
    return this.authorizationService.isLocationAdmin();
  }

  public triggerRecordIdEvent() {
    this.recordIdEditWarning.emit(true);
  }

  changeSelectedIntendedUsageType($event: Event) {
    this.selectIntendedUsage($event.target['value']);
  }

  selectIntendedUsage(intendedUsageTypeParsed) {
    switch (intendedUsageTypeParsed) {
      case TranslatableIntendedUsageType.REALTIME:
        this.location.intendedUsageType = IntendedUsageType.REALTIME;
        return;
      case TranslatableIntendedUsageType.ANALYTICS:
        this.location.intendedUsageType = IntendedUsageType.ANALYTICS;
        return;
      default:
        // Fallback to contract default. Intended usage on location is empty
        this.location.intendedUsageType = null;
        return;
    }
  }

  private initializeIntendedUsageTypes() {
    this.selectableIntendedUsageTypes = [
      TranslatableIntendedUsageType.REALTIME,
      TranslatableIntendedUsageType.ANALYTICS
    ];
    // add default intended usage type from contract
    const contractType = IntendedUsageUtil.convertIntendedUsageType(this.location.contract.intendedUsageType, true);
    if (contractType) {
      this.selectableIntendedUsageTypes.push(contractType);
    }
    this.setSelectedIntendedUsageType();
  }

  private setSelectedIntendedUsageType() {
    this.selectedIntendedUsageType = IntendedUsageUtil.parseIntendedUsageType(this.location);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/linear-details/general/linear-details-general.component.ts